/**
 * Sales Team Report payload types.
 *
 * Mirror the executor's data shape in
 * `lambda/reports/executors/sales-team-funnel-report.ts` — the Forecast page's
 * Sales Funnel grouped by sales team (team = sales manager) per location.
 *
 * The report has two modes. RECENT (a single report date) is the default and
 * unchanged. RANGE (startDate + endDate) additionally returns `periods` — the
 * month-relative week columns — and per-week counts on every row and totals
 * object. `periods` being present IS the mode discriminator; there is no mode
 * field. When it is absent the payload is exactly the pre-range-mode shape, so
 * the renderer falls back to the 11-column table and a backend-first deploy is
 * safe.
 *
 * The per-week fields are declared here as intersections rather than being
 * added to SalesFunnelRow / SalesFunnelTotals in services/api.ts, because
 * GET /sales-funnel (the Forecast page card) never returns them. That keeps the
 * Forecast contract honest and keeps api.ts — already over the 1000-line
 * limit — from growing.
 */

import { SalesFunnelRow, SalesFunnelTotals, SalesTeamSection } from '../services/api';

// SalesTeamSection is shared with the GET /sales-funnel response — one
// definition in services/api.ts, re-exported here for report-side imports.
export type { SalesTeamSection } from '../services/api';

/**
 * Per-day lead/visit maps do not exist on VIN_LEADS records before this date
 * (verified against production for all 8 rooftops, 2026-09-17). Single source
 * of truth for the start-date picker's `min`, the client-side validation, and
 * the pre-history banner — do not inline the literal anywhere.
 */
export const FUNNEL_HISTORY_START = '2026-06-01';

/** Matches FUNNEL_RANGE_MAX_DAYS in lambda/shared/funnel-periods.ts. */
export const FUNNEL_MAX_RANGE_DAYS = 92;

/** The metrics a week column can display, one at a time. */
export type FunnelMetricKey = 'sales' | 'leads' | 'visits' | 'apptsPast';

export const FUNNEL_METRIC_OPTIONS: { key: FunnelMetricKey; label: string }[] = [
  { key: 'sales', label: 'Sales' },
  { key: 'leads', label: 'Leads' },
  { key: 'visits', label: 'Visits' },
  { key: 'apptsPast', label: 'Appts' },
];

/** One month-relative week column. Mirrors the backend FunnelPeriod. */
export interface FunnelPeriod {
  /** `2026-08-W1` — unique and lexicographically chronological. */
  key: string;
  year: number;
  month: number;
  /** Month-relative week ordinal, 1-5. */
  week: number;
  /** Column header, e.g. `Aug W1`. Year-less — see `year` for disambiguation. */
  label: string;
  /** The clipped span, e.g. `Aug 1–7`, `Aug 3–7`, `Feb 29`. */
  sublabel: string;
  startDate: string;
  endDate: string;
  /** Inclusive day count after clipping, 1-7. */
  days: number;
  /** True when the requested range clipped this bucket below its natural span. */
  partial: boolean;
}

/** Per-(salesperson, week) counts, index-aligned with the top-level `periods`. */
export interface FunnelPeriodMetrics {
  leads: number;
  visits: number;
  apptsPast: number;
  sales: number;
}

/** A funnel row that may carry per-week counts (range mode). */
export type SalesFunnelRowP = SalesFunnelRow & { periods?: FunnelPeriodMetrics[] };

/** Totals that may carry per-week counts (range mode). */
export type SalesFunnelTotalsP = SalesFunnelTotals & { periods?: FunnelPeriodMetrics[] };

/** A team section whose rows and subtotals may carry per-week counts. */
export interface SalesTeamSectionP extends Omit<SalesTeamSection, 'rows' | 'subtotals'> {
  rows: SalesFunnelRowP[];
  subtotals: SalesFunnelTotalsP;
}

export interface SalesTeamLocationSection {
  locationId: number;
  locationName: string;
  /** Recent mode: the 5 working days. Range mode: EVERY calendar day (up to 92). */
  pastDates: string[];
  nextDates: string[];
  /** Sorted by managerName, Unassigned last. */
  teams: SalesTeamSectionP[];
  totals: SalesFunnelTotalsP;
  lastUpdatedAt: { leads?: string; appointments?: string };
}

export interface SalesTeamFunnelReportData {
  /** In range mode this is the range's END date, which anchors the outlook. */
  reportDate: string;
  locations: SalesTeamLocationSection[];
  grandTotals: SalesFunnelTotalsP;
  /**
   * Month-relative week columns. Range mode only, and its presence is the mode
   * discriminator. Lives once at the top level because the bucketing is pure
   * calendar math and therefore identical for every location.
   */
  periods?: FunnelPeriod[];
  rangeStartDate?: string;
  rangeEndDate?: string;
  /**
   * Range mode only: the window `apptsPast` actually covers, which ends the day
   * BEFORE the range end — appointments on the end date report with the Next 3 /
   * Next 5 outlook instead, matching the Forecast card. Footnote it from these
   * exact dates rather than re-deriving the rule.
   */
  apptsPastWindow?: { startDate: string; endDate: string };
}
